import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, Spinner, Button,
} from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import {
  createInvoice,
  updateInvoice,
  uploadFileToInvoice,
  removeInvoiceAttachment,
  getInvoiceSignedUrls,
  uploadStagingFile,
  getStagingSignedUrls,
  removeStagingObject,
  extractInvoiceFromFile,
  getPayrollCycles,
  formatCurrency,
  computeLineMoney,
  computeLineTotal,
} from "./Api";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? BLUE : "#E2E8F0",
    borderRadius: 10,
    minHeight: 46,
    fontSize: "0.85rem",
    boxShadow: isFocused ? "0 0 0 2px rgba(37,99,235,0.15)" : "none",
    backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
    "&:hover": { borderColor: BLUE },
  }),
  valueContainer: (base) => ({ ...base, paddingLeft: 36 }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.85rem",
    background: isSelected ? BLUE : isFocused ? "#EFF6FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 10, border: "1px solid #E2E8F0", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const LABEL_STYLE = {
  fontSize: "0.8rem", fontWeight: 600, color: "#334155", marginBottom: 6,
};

const FIELD_LABEL = { fontSize: "0.75rem", color: "#334155", fontWeight: 600, marginBottom: 5 };

const INPUT_STYLE = {
  borderColor: "#E2E8F0",
  borderRadius: 10,
  fontSize: "0.85rem",
  height: 46,
};

const INPUT_ICON_STYLE = { ...INPUT_STYLE, paddingLeft: 38 };

const CARD_STYLE = {
  background: "#fff", border: "1px solid #E7EAF0", borderRadius: 14, padding: "18px 20px",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const SECTION_TITLE = { fontSize: "0.95rem", fontWeight: 800, color: "#0F172A" };

const ROW_BTN = {
  width: 40, height: 46, borderRadius: 10,
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", flexShrink: 0,
};

const SectionBadge = ({ n }) => (
  <span style={{
    width: 24, height: 24, borderRadius: "50%", background: BLUE, color: "#fff",
    fontSize: "0.72rem", fontWeight: 800,
    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  }}>{n}</span>
);

// Overlays a small leading icon on the wrapped input/select (which must reserve
// left padding for it).
const WithIcon = ({ icon, children }) => (
  <div style={{ position: "relative" }}>
    {children}
    <FeatherIcon
      icon={icon} size={15} color="#94A3B8"
      style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
    />
  </div>
);

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const FilePreviewModal = ({ isOpen, toggle, signedUrl, fileKey }) => {
  const type = fileKey ? getFileTypeFromKey(fileKey) : "unknown";
  const fileName = fileKey ? fileKey.split("/").pop() : "Attachment";
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>{fileName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {signedUrl ? (
          <Previewer signedUrl={signedUrl} type={type} height="72vh" width="100%" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "72vh" }}>
            <Spinner style={{ color: "#A5B4FC" }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={fileName}
            style={{
              textDecoration: "none", background: BLUE, color: "#fff", borderRadius: 8,
              padding: "8px 14px", fontSize: "0.8rem", fontWeight: 700,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FeatherIcon icon="download" size={14} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

// Confidence < this is flagged so the reviewer double-checks that field.
const LOW_CONF = 0.5;

const ConfidencePill = ({ score }) => {
  if (typeof score !== "number") return null;
  const low = score < LOW_CONF;
  return (
    <span style={{
      fontSize: "0.66rem", fontWeight: 700, borderRadius: 20, padding: "2px 8px",
      background: low ? "#FEF3C7" : "#DCFCE7",
      color: low ? "#B45309" : "#15803D",
    }}>
      {low ? "Please confirm" : "High confidence"}
    </span>
  );
};

const DetailRow = ({ label, value, muted, badge }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid #F1F5F9" }}>
    <span style={{ fontSize: "0.72rem", color: "#64748B", fontWeight: 600, width: 120, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: "0.82rem", color: muted ? "#94A3B8" : "#0F172A", fontWeight: 600, flex: 1, minWidth: 0 }}>
      {value}
    </span>
    {badge}
  </div>
);

// Shows what the AI read from the invoice and lets the user accept it into the
// form or dismiss it and fill manually.
const PrefillReviewModal = ({ isOpen, suggestion, onConfirm, onDismiss }) => {
  if (!isOpen || !suggestion) return null;
  const conf = suggestion.confidence || {};
  const groups = suggestion.groups || [];

  const grandTotal = groups.reduce((sum, g) =>
    sum + (g.rows || []).reduce((a, r) =>
      a + (computeLineMoney(computeLineTotal(r.hours, r.minutes, r.amountPerHour, r.materialCost), r.gstRate).total || 0), 0
    ), 0);

  const vendorValue = suggestion.vendorLabel
    ? suggestion.vendorLabel
    : suggestion.vendorNameOnInvoice
      ? `${suggestion.vendorNameOnInvoice} — not in your vendor list`
      : "Not detected";

  return (
    <Modal isOpen={isOpen} centered size="lg" backdrop="static" style={{ zIndex: 13000 }}
      contentClassName="iv-form-modal-content">
      <ModalHeader tag="div" style={{ borderBottom: "1px solid #EEF2F7", padding: "16px 22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: "linear-gradient(135deg,#6366F1,#2563EB)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
          }}>
            <FeatherIcon icon="zap" size={19} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "1.02rem", fontWeight: 800, color: "#0F172A" }}>We read your invoice</div>
            <div style={{ fontSize: "0.76rem", color: "#64748B", fontWeight: 500, marginTop: 2 }}>
              Review the details below, then choose to prefill or fill manually.
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody style={{ padding: "18px 22px", maxHeight: "60vh", overflowY: "auto" }}>
        <div style={{ ...CARD_STYLE, padding: "12px 16px", marginBottom: 12 }}>
          <DetailRow label="Vendor" value={vendorValue} muted={!suggestion.vendorLabel} badge={<ConfidencePill score={conf.vendor} />} />
          <DetailRow label="Category" value={suggestion.categoryLabel || "Not detected"} muted={!suggestion.categoryLabel} badge={<ConfidencePill score={conf.category} />} />
          <DetailRow label="Title" value={suggestion.title || "—"} muted={!suggestion.title} />
          {suggestion.description ? (
            <DetailRow label="Notes" value={suggestion.description} />
          ) : null}
        </div>

        {groups.map((g, gi) => (
          <div key={gi} style={{ ...CARD_STYLE, padding: "12px 16px", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FeatherIcon icon="home" size={14} color={BLUE} />
              <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#0F172A" }}>
                {g.propertyLabel || "Property not matched"}
              </span>
              {g.buildingName ? <span style={{ fontSize: "0.78rem", color: "#475569" }}>· {g.buildingName}</span> : null}
              <ConfidencePill score={conf.property} />
            </div>
            <DetailRow label="Invoice No." value={g.referenceNo || "—"} muted={!g.referenceNo} />
            {(g.rows || []).map((r, ri) => {
              const total = computeLineMoney(computeLineTotal(r.hours, r.minutes, r.amountPerHour, r.materialCost), r.gstRate).total;
              const parts = [];
              if (r.unitNo) parts.push(`Unit ${r.unitNo}`);
              if (Number(r.hours) === 1 && !r.materialCost) parts.push(`Flat ${formatCurrency(Number(r.amountPerHour) || 0)}`);
              else {
                if (r.hours !== "" && r.hours != null) parts.push(`${r.hours}h${r.minutes ? ` ${r.minutes}m` : ""}`);
                if (r.amountPerHour !== "" && r.amountPerHour != null) parts.push(`@ ${formatCurrency(Number(r.amountPerHour) || 0)}/hr`);
                if (r.materialCost) parts.push(`+ ${formatCurrency(Number(r.materialCost) || 0)} materials`);
              }
              parts.push(`GST ${r.gstRate || 0}%`);
              return (
                <div key={ri} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: "0.78rem" }}>
                  <span style={{ color: "#475569" }}>{parts.join(" · ")}</span>
                  <span style={{ fontWeight: 800, color: "#0F172A" }}>{formatCurrency(total)}</span>
                </div>
              );
            })}
          </div>
        ))}

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#1E3A8A", borderRadius: 10, padding: "12px 18px", marginTop: 4,
        }}>
          <span style={{ color: "#FCD34D", fontWeight: 700, fontSize: "0.78rem" }}>Detected Grand Total</span>
          <span style={{ color: "#FBBF24", fontWeight: 900, fontSize: "1.1rem" }}>{formatCurrency(grandTotal)}</span>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-start" }}>
          <FeatherIcon icon="info" size={14} color="#94A3B8" style={{ marginTop: 2, flexShrink: 0 }} />
          <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
            AI suggestions can be wrong — anything you prefill stays fully editable, and flagged fields are worth a second look.
          </span>
        </div>
      </ModalBody>
      <ModalFooter style={{ borderTop: "1px solid #E9EEF5", padding: "14px 22px", background: "#F8FAFC", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" onClick={onDismiss} style={{
          height: 42, fontWeight: 600, fontSize: "0.84rem", borderRadius: 10, padding: "0 18px",
          background: "#fff", border: "1px solid #E2E8F0", color: "#334155", cursor: "pointer",
        }}>
          No, I'll fill manually
        </button>
        <button type="button" onClick={onConfirm} style={{
          height: 42, fontWeight: 700, fontSize: "0.84rem", borderRadius: 10, padding: "0 20px",
          background: BLUE, border: "none", color: "#fff", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
        }}>
          <FeatherIcon icon="check" size={15} /> Yes, prefill now
        </button>
      </ModalFooter>
    </Modal>
  );
};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Labels for the payments-cycle card (cycle dates arrive as YYYY-MM-DD).
const cycleMonthLongLabel = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};
const cycleDayLabel = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const blankRow = () => ({
  id: uid(),
  unitNo: "",
  hours: "",
  minutes: "",
  amountPerHour: "",
  materialCost: "",
  gstRate: 0,
  comment: "",
  files: [],
});

// One property/building section holding one or more unit rows. Each section has
// its own vendor Invoice No.; on create, every row becomes its own invoice split.
const blankGroup = (propertyId = "", buildingName = "") => ({
  id: uid(),
  propertyId,
  buildingName,
  referenceNo: "",
  rows: [blankRow()],
});

const InvoiceFormModal = ({
  isOpen,
  onClose,
  invoiceData,
  isNew,
  properties = [],
  vendors,
  vendorLocked = false,
  vendorName = "",
  vendorCategoryName = "",
  vendorCategories = [],
  prefilledPropertyId,
  prefilledBuildingName,
  prefilledPropertyLabel,
}) => {
  const initial = useMemo(() => ({
    title: "",
    vendorId: "",
    categoryId: "",
    description: "",
    groups: [prefilledPropertyId ? blankGroup(prefilledPropertyId, prefilledBuildingName || "") : blankGroup()],
  }), [prefilledPropertyId, prefilledBuildingName]);

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const submitLock = useRef(false);
  const [createdId, setCreatedId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewStaged, setPreviewStaged] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [aiScanning, setAiScanning] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  // The Payments Calendar cycle a newly-created invoice will land in.
  const [cycleInfo, setCycleInfo] = useState(null);
  const fileInputRef = useRef(null);
  const rowFileInputRef = useRef(null);
  const rowFileTargetRef = useRef(null);
  // Keys already sent to the AI, so a given upload is scanned at most once.
  const scannedKeysRef = useRef(new Set());

  const cleanupStaged = (files) => {
    (files || []).forEach((f) => {
      if (f.key) removeStagingObject(f.key).catch(() => {});
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isNew) {
      setForm(initial);
      setCreatedId(null);
      setAttachments([]);
      setSignedUrls({});
      cleanupStaged(pendingFiles);
      setPendingFiles([]);
      setAiSuggestion(null);
      setAiScanning(false);
      scannedKeysRef.current = new Set();
    } else if (invoiceData) {
      const refs = Array.isArray(invoiceData.properties) && invoiceData.properties.length
        ? invoiceData.properties
        : [];
      const firstRef = refs[0] || {
        propertyId: prefilledPropertyId || "",
        buildingName: prefilledBuildingName || "",
      };
      setForm({
        title: invoiceData.title || "",
        vendorId: invoiceData.vendorId || invoiceData.vendor?._id || "",
        categoryId: invoiceData.categoryId || invoiceData.category?._id || "",
        description: invoiceData.description || "",
        groups: [{
          id: uid(),
          propertyId: firstRef.propertyId || "",
          buildingName: firstRef.buildingName || "",
          referenceNo: invoiceData.referenceNo || "",
          rows: [{
            id: uid(),
            unitNo: firstRef.unitNo || "",
            hours: invoiceData.hours ?? "",
            minutes: invoiceData.minutes ?? "",
            amountPerHour: invoiceData.amountPerHour ?? "",
            materialCost: invoiceData.materialCost ?? "",
            gstRate: invoiceData.gstRate ?? 0,
            comment: invoiceData.comment || "",
            files: [],
          }],
        }],
      });
      setCreatedId(invoiceData._id);
      setAttachments(invoiceData.attachments || []);
    }
    setErrors({});
  }, [isOpen, isNew, invoiceData, initial, prefilledPropertyId, prefilledBuildingName]);

  useEffect(() => {
    if (!attachments.length) {
      setSignedUrls({});
      return;
    }
    const missing = attachments.filter((k) => !signedUrls[k]);
    if (!missing.length) return;
    (async () => {
      const urls = await getInvoiceSignedUrls(missing);
      setSignedUrls((prev) => ({ ...prev, ...urls }));
    })();
  }, [attachments]); // eslint-disable-line

  // Resolve which Payments Calendar cycle a new invoice will fall into: the first
  // upcoming cycle whose Submit-By cutoff hasn't passed (invoices submitted after
  // a Submit By roll into the next cycle).
  useEffect(() => {
    if (!isOpen || !isNew) {
      setCycleInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getPayrollCycles({ status: "upcoming", limit: 24 });
        if (cancelled) return;
        const today = res?.today || "";
        const list = Array.isArray(res?.data) ? res.data : [];
        const target =
          (today ? list.find((c) => c.submitBy >= today) : null) || list[0] || null;
        setCycleInfo(
          target
            ? {
                month: cycleMonthLongLabel(target.paymentDate),
                submitBy: cycleDayLabel(target.submitBy),
                approveBy: cycleDayLabel(target.approveBy),
                pay: cycleDayLabel(target.paymentDate),
              }
            : null
        );
      } catch {
        if (!cancelled) setCycleInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isNew]);

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const propertyOptions = useMemo(
    () => (properties || [])
      .map((p) => ({ value: p._id, label: p.title || p.name || p._id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [properties]
  );

  // Accepts both the `building: [{buildingName}]` shape (internal property list)
  // and the `buildings: ["A", "B"]` shape (vendor eligibility list).
  const buildingOptionsFor = (propertyId) => {
    const prop = (properties || []).find((p) => p._id === propertyId);
    if (!prop) return [];
    const raw = Array.isArray(prop.buildings) && prop.buildings.length
      ? prop.buildings.map((b) => (typeof b === "string" ? b : b?.buildingName))
      : (prop.building || []).map((b) => b?.buildingName);
    return raw.filter(Boolean).map((b) => ({ value: b, label: b }));
  };

  const updateGroup = (gi, patch) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)),
    }));

  const updateRow = (gi, ri, patch) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) =>
        i === gi
          ? { ...g, rows: g.rows.map((r, j) => (j === ri ? { ...r, ...patch } : r)) }
          : g
      ),
    }));

  const addRow = (gi) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) => (i === gi ? { ...g, rows: [...g.rows, blankRow()] } : g)),
    }));

  const removeRow = (gi, ri) => {
    const group = form.groups[gi];
    if (!group || group.rows.length <= 1) return;
    cleanupStaged(group.rows[ri]?.files);
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g, i) =>
        i === gi ? { ...g, rows: g.rows.filter((_, j) => j !== ri) } : g
      ),
    }));
  };

  const addGroup = () =>
    setForm((p) => ({ ...p, groups: [...p.groups, blankGroup()] }));

  const removeGroup = (gi) => {
    if (form.groups.length <= 1) return;
    (form.groups[gi]?.rows || []).forEach((r) => cleanupStaged(r.files));
    setForm((p) => ({ ...p, groups: p.groups.filter((_, i) => i !== gi) }));
  };

  // When a property is chosen and it has exactly one building, auto-select it.
  const onPickProperty = (gi, propertyId) => {
    const buildings = buildingOptionsFor(propertyId);
    updateGroup(gi, {
      propertyId,
      buildingName: buildings.length === 1 ? buildings[0].value : "",
    });
  };

  // ── Per-row supporting attachments (staged, adopted by the split on create) ──
  const patchRowFiles = (groupId, rowId, updater) =>
    setForm((p) => ({
      ...p,
      groups: p.groups.map((g) =>
        g.id !== groupId
          ? g
          : { ...g, rows: g.rows.map((r) => (r.id !== rowId ? r : { ...r, files: updater(r.files || []) })) }
      ),
    }));

  const openRowFilePicker = (groupId, rowId) => {
    rowFileTargetRef.current = { groupId, rowId };
    rowFileInputRef.current?.click();
  };

  const handleRowFiles = async (fileList) => {
    const target = rowFileTargetRef.current;
    if (rowFileInputRef.current) rowFileInputRef.current.value = "";
    if (!target || !fileList?.length) return;
    const { groupId, rowId } = target;
    for (const file of Array.from(fileList)) {
      const pf = { id: uid(), name: file.name, key: null, signedUrl: null, uploading: true };
      patchRowFiles(groupId, rowId, (prev) => [...prev, pf]);
      try {
        const key = await uploadStagingFile(file);
        const urls = await getStagingSignedUrls([key]);
        patchRowFiles(groupId, rowId, (prev) =>
          prev.map((f) => (f.id === pf.id ? { ...f, key, signedUrl: urls[key] || null, uploading: false } : f))
        );
      } catch (err) {
        console.error("row staging upload failed", err);
        patchRowFiles(groupId, rowId, (prev) => prev.filter((f) => f.id !== pf.id));
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const removeRowFile = (groupId, rowId, fileId) => {
    const group = (form.groups || []).find((g) => g.id === groupId);
    const row = (group?.rows || []).find((r) => r.id === rowId);
    const file = (row?.files || []).find((f) => f.id === fileId);
    if (file?.key) removeStagingObject(file.key).catch(() => {});
    patchRowFiles(groupId, rowId, (prev) => prev.filter((f) => f.id !== fileId));
  };

  const anyRowFileUploading = (form.groups || []).some((g) =>
    (g.rows || []).some((r) => (r.files || []).some((f) => f.uploading))
  );

  // Vendor options (active vendors). When editing an invoice whose vendor is no
  // longer active, keep a synthetic option so the selection still shows.
  const vendorOpts = useMemo(() => {
    const list = (vendors || [])
      .map((v) => ({
        value: v._id,
        label: v.name,
        categories:
          (v.categories && v.categories.length
            ? v.categories
            : v.category
              ? [v.category]
              : []) || [],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (!isNew && invoiceData?.vendorId && !list.some((o) => o.value === invoiceData.vendorId)) {
      list.unshift({
        value: invoiceData.vendorId,
        label: `${invoiceData.vendorName || "Vendor"} (inactive)`,
        categories: invoiceData.category ? [invoiceData.category] : [],
      });
    }
    return list;
  }, [vendors, isNew, invoiceData]);

  const selectedVendor = vendorOpts.find((o) => o.value === form.vendorId) || null;

  // Category choices: from the selected vendor (internal create), or the
  // eligibility-provided list (vendor self-submission).
  const categoryChoices = useMemo(() => {
    const fromVendor = vendorLocked ? vendorCategories : (selectedVendor?.categories || []);
    return (fromVendor || [])
      .map((c) => ({ value: c._id, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [vendorLocked, vendorCategories, selectedVendor]);

  // Keep the chosen category consistent with the available choices: drop a stale
  // category when the vendor changes, and auto-select when only one is available.
  useEffect(() => {
    setForm((p) => {
      const valid = categoryChoices.some((o) => o.value === p.categoryId);
      let categoryId = valid ? p.categoryId : "";
      if (!categoryId && categoryChoices.length === 1) categoryId = categoryChoices[0].value;
      return categoryId === p.categoryId ? p : { ...p, categoryId };
    });
  }, [categoryChoices]); // eslint-disable-line

  // Live GST + total breakdown across every unit row.
  const allRows = (form.groups || []).flatMap((g) => g.rows || []);
  const lineMoney = allRows.map((r) =>
    computeLineMoney(computeLineTotal(r.hours, r.minutes, r.amountPerHour, r.materialCost), r.gstRate)
  );
  const subtotalSum = lineMoney.reduce((a, m) => a + (m.subtotal || 0), 0);
  const gstSum = lineMoney.reduce((a, m) => a + (m.gstAmount || 0), 0);
  const totalSum = lineMoney.reduce((a, m) => a + (m.total || 0), 0);
  const totalRowCount = allRows.length;

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title is required";
    if (!vendorLocked && !form.vendorId) e.vendorId = "Vendor is required";
    if (!form.categoryId) e.categoryId = "Category is required";
    const groups = form.groups || [];
    if (!groups.length) {
      e.lines = "Add at least one property";
    } else {
      const seen = new Set();
      for (const g of groups) {
        if (e.lines) break;
        if (!g.propertyId) { e.lines = "Select a property for every section"; break; }
        if (!g.buildingName?.trim()) { e.lines = "Choose a building for every property"; break; }
        if (!g.referenceNo?.trim()) { e.lines = "Enter an Invoice No. for every property section"; break; }
        for (const r of g.rows || []) {
          if (r.hours === "" || !Number.isInteger(Number(r.hours)) || Number(r.hours) < 0) {
            e.lines = "Enter whole-number hours for every row"; break;
          }
          if (r.minutes !== "" && r.minutes != null &&
              (!Number.isInteger(Number(r.minutes)) || Number(r.minutes) < 0 || Number(r.minutes) > 59)) {
            e.lines = "Minutes must be a whole number between 0 and 59"; break;
          }
          if (r.amountPerHour === "" || Number.isNaN(Number(r.amountPerHour)) || Number(r.amountPerHour) < 0) {
            e.lines = "Enter a valid amount per hour for every row"; break;
          }
          if (r.materialCost !== "" && r.materialCost != null &&
              (Number.isNaN(Number(r.materialCost)) || Number(r.materialCost) < 0)) {
            e.lines = "Material cost must be a valid positive amount"; break;
          }
          const rate = Number(r.gstRate);
          if (Number.isNaN(rate) || rate < 0 || rate > 100) {
            e.lines = "GST must be between 0 and 100%"; break;
          }
          const key = `${g.propertyId}::${g.buildingName.trim().toLowerCase()}::${(r.unitNo || "").trim().toLowerCase()}`;
          if (seen.has(key)) { e.lines = "Remove duplicate rows (same property, building and unit)"; break; }
          seen.add(key);
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (submitLock.current) return;
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (pendingFiles.some((p) => p.uploading) || anyRowFileUploading) {
      toast.warning("Please wait for the files to finish uploading.");
      return;
    }
    submitLock.current = true;
    setSaving(true);
    try {
      const stagedKeys = pendingFiles.map((p) => p.key).filter(Boolean);
      if (isNew && !createdId) {
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          lines: form.groups.flatMap((g) =>
            g.rows.map((r) => ({
              propertyId: g.propertyId,
              buildingName: g.buildingName.trim(),
              referenceNo: (g.referenceNo || "").trim(),
              unitNo: (r.unitNo || "").trim(),
              hours: Number(r.hours),
              minutes: Number(r.minutes) || 0,
              amountPerHour: Number(r.amountPerHour),
              materialCost: Number(r.materialCost) || 0,
              gstRate: Number(r.gstRate),
              comment: (r.comment || "").trim(),
              attachments: (r.files || []).map((f) => f.key).filter(Boolean),
            }))
          ),
          attachments: stagedKeys,
          categoryId: form.categoryId,
        };
        if (!vendorLocked) payload.vendorId = form.vendorId;
        const res = await createInvoice(payload);
        const created = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        if (!created.length) {
          toast.error("Invoice was not created.");
          return;
        }
        setPendingFiles([]);
        toast.success(
          created.length > 1
            ? `Submitted ${created.length} invoices under Invoice #${res.invoiceNo || ""}.`
            : "Invoice submitted for approval."
        );
        onClose(true);
      } else {
        const group = form.groups[0] || {};
        const row = (group.rows || [])[0] || {};
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          referenceNo: (group.referenceNo || "").trim(),
          hours: Number(row.hours),
          minutes: Number(row.minutes) || 0,
          amountPerHour: Number(row.amountPerHour),
          materialCost: Number(row.materialCost) || 0,
          gstRate: Number(row.gstRate),
          comment: (row.comment || "").trim(),
          unitNo: (row.unitNo || "").trim(),
          properties: [{ propertyId: group.propertyId, buildingName: (group.buildingName || "").trim(), unitNo: (row.unitNo || "").trim() }],
          categoryId: form.categoryId,
        };
        if (!vendorLocked) payload.vendorId = form.vendorId;
        const targetId = invoiceData?._id || createdId;
        await updateInvoice(targetId, payload);
        toast.success("Invoice updated.");
        onClose(true);
      }
    } catch {
      // toast handled in api helper
    } finally {
      setSaving(false);
      submitLock.current = false;
    }
  };

  const invoiceId = createdId || invoiceData?._id;

  // True when the user hasn't started filling the form yet — the only time we
  // auto-open the AI suggestion so we never interrupt someone mid-entry.
  const isFormPristine = () =>
    !form.title?.trim() &&
    !form.vendorId &&
    (form.groups || []).every(
      (g) =>
        !g.propertyId &&
        !String(g.referenceNo || "").trim() &&
        (g.rows || []).every(
          (r) =>
            !String(r.unitNo || "").trim() &&
            r.hours === "" &&
            r.amountPerHour === ""
        )
    );

  // Ask the server to read the staged invoice file. Quiet by design: a miss just
  // means the user fills the form as usual.
  const runAiScan = async (key) => {
    if (!key || aiScanning || scannedKeysRef.current.has(key)) return;
    scannedKeysRef.current.add(key);
    setAiScanning(true);
    try {
      const suggestion = await extractInvoiceFromFile(key);
      if (suggestion) {
        setAiSuggestion(suggestion);
      } else {
        toast.info("Couldn't read the invoice automatically — please fill the details in.");
      }
    } finally {
      setAiScanning(false);
    }
  };

  // Maps a validated AI suggestion into the form, constraining every choice to
  // options that actually exist (unknown property/building fall back to blank so
  // a wrong guess is never silently committed). Vendor is left untouched when the
  // vendor is locked (self-submission).
  const applySuggestion = (s) => {
    setForm((prev) => {
      const groups = (s.groups && s.groups.length ? s.groups : [{}]).map((g) => {
        const propertyId = propertyOptions.some((o) => o.value === g.propertyId) ? g.propertyId : "";
        const buildings = propertyId ? buildingOptionsFor(propertyId) : [];
        let buildingName = "";
        if (propertyId) {
          if (g.buildingName && buildings.some((b) => b.value === g.buildingName)) buildingName = g.buildingName;
          else if (buildings.length === 1) buildingName = buildings[0].value;
        }
        const rows = (g.rows && g.rows.length ? g.rows : [{}]).map((r) => ({
          ...blankRow(),
          unitNo: r.unitNo ?? "",
          hours: r.hours ?? "",
          minutes: r.minutes ?? "",
          amountPerHour: r.amountPerHour ?? "",
          materialCost: r.materialCost ?? "",
          gstRate: r.gstRate ?? 0,
          comment: r.comment ?? "",
        }));
        return { id: uid(), propertyId, buildingName, referenceNo: g.referenceNo || "", rows };
      });
      return {
        ...prev,
        title: s.title || prev.title,
        description: s.description || prev.description,
        vendorId: vendorLocked ? prev.vendorId : (s.vendorId || prev.vendorId || ""),
        categoryId: s.categoryId || prev.categoryId || "",
        groups: groups.length ? groups : prev.groups,
      };
    });
    setAiSuggestion(null);
    toast.success("Details prefilled — review and adjust before submitting.");
  };

  // A staged invoice file that's finished uploading can be (re)scanned on demand.
  const stagedScanKey = pendingFiles.find((p) => p.key && !p.uploading)?.key || null;
  const aiScanReady = isNew && !!stagedScanKey;
  const triggerManualScan = () => {
    if (!stagedScanKey) return;
    scannedKeysRef.current.delete(stagedScanKey);
    runAiScan(stagedScanKey);
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;

    if (pendingFiles.length > 0 || attachments.length > 0) {
      toast.warning("Only one invoice file is allowed — remove the current file to replace.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const file = files[0];

    if (!invoiceId) {
      // New invoice: upload the file to a temporary staging area right away so it
      // gets a signed URL and previews exactly like a saved attachment. The key
      // is adopted by the invoice on create.
      const pf = {
        id: uid(),
        file,
        name: file.name,
        key: null,
        signedUrl: null,
        uploading: true,
        error: false,
      };
      setPendingFiles((prev) => [...prev, pf]);
      if (fileInputRef.current) fileInputRef.current.value = "";

      try {
        const key = await uploadStagingFile(pf.file);
        const urls = await getStagingSignedUrls([key]);
        setPendingFiles((prev) =>
          prev.map((p) =>
            p.id === pf.id ? { ...p, key, signedUrl: urls[key] || null, uploading: false } : p
          )
        );
        // Upload-first flow: read the invoice with AI and offer to prefill.
        if (isFormPristine()) runAiScan(key);
      } catch (err) {
        console.error("staging upload failed", err);
        setPendingFiles((prev) =>
          prev.map((p) => (p.id === pf.id ? { ...p, uploading: false, error: true } : p))
        );
        toast.error(`Failed to upload ${pf.name}`);
      }
      return;
    }

    setUploading(true);
    try {
      const key = await uploadFileToInvoice(invoiceId, file);
      setAttachments((prev) => [...prev, key]);
      toast.success("File uploaded");
    } catch (err) {
      console.error("upload failed", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.key) removeStagingObject(removed.key).catch(() => {});
      return prev.filter((p) => p.id !== id);
    });
  };

  const openPendingPreview = (pf) => {
    if (pf?.signedUrl) setPreviewStaged({ signedUrl: pf.signedUrl, name: pf.name });
  };

  const handleRemove = async (key) => {
    try {
      await removeInvoiceAttachment(invoiceId, key);
      setAttachments((prev) => prev.filter((k) => k !== key));
      toast.success("Attachment removed");
    } catch {
      // handled
    }
  };

  // Header context shows the first section's property (with a "+N more" hint when
  // the submission spans several properties); the full editor lives in the body.
  const linkedGroups = (form.groups || []).filter((g) => g.propertyId);
  const firstPropLabel = linkedGroups.length
    ? propertyOptions.find((o) => o.value === linkedGroups[0].propertyId)?.label || ""
    : "";
  const headerPropertyLabel = firstPropLabel
    ? `${firstPropLabel}${linkedGroups.length > 1 ? ` +${linkedGroups.length - 1} more` : ""}`
    : (isNew ? prefilledPropertyLabel || "" : "");
  const headerBuildingName = linkedGroups.length > 1 ? "" : (linkedGroups[0]?.buildingName || "");

  const handleClose = (refresh) => {
    if (isNew && !createdId) {
      cleanupStaged(pendingFiles);
      (form.groups || []).forEach((g) => (g.rows || []).forEach((r) => cleanupStaged(r.files)));
    }
    setPendingFiles([]);
    onClose(refresh);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={() => handleClose(false)} size="xl" centered backdrop="static"
        className="iv-form-modal-dialog" contentClassName="iv-form-modal-content">
        <ModalHeader toggle={() => handleClose(false)} style={{
          borderBottom: "1px solid #EEF2F7", padding: "16px 24px 14px",
          background: "#fff", borderRadius: "14px 14px 0 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, width: "100%" }}>
            <div style={{
              width: 46, height: 46, borderRadius: 12, flexShrink: 0,
              background: BLUE,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
            }}>
              <FeatherIcon icon={isNew ? "file-plus" : "edit-2"} size={20} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>
                {isNew ? "New Invoice" : `Edit Invoice ${invoiceData?.invoiceNo ? `#${invoiceData.invoiceNo}` : ""}`}
              </div>
              {(headerPropertyLabel || headerBuildingName) ? (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, marginTop: 5,
                  fontSize: "0.78rem", color: "#475569", fontWeight: 600,
                }}>
                  <FeatherIcon icon="map-pin" size={12} color={BLUE} style={{ flexShrink: 0 }} />
                  <span style={{ color: "#0F172A" }}>{headerPropertyLabel || "—"}</span>
                  {headerBuildingName && (
                    <>
                      <span style={{ color: "#CBD5E1" }}>·</span>
                      <FeatherIcon icon="home" size={12} color="#64748B" style={{ flexShrink: 0 }} />
                      <span>{headerBuildingName}</span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: "0.78rem", color: "#64748B", fontWeight: 500, marginTop: 3 }}>
                  {isNew ? "Fill in the details below and attach the invoice file" : "Update invoice details"}
                </div>
              )}
            </div>
            {isNew && cycleInfo && (
              <div style={{ marginLeft: "auto", marginRight: 26, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: "0.56rem", fontWeight: 800, letterSpacing: "0.6px", color: "#94A3B8", textTransform: "uppercase" }}>
                    Payments Cycle
                  </span>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#CBD5E1" }} />
                  <span style={{ fontSize: "0.74rem", fontWeight: 800, color: "#0F172A" }}>{cycleInfo.month}</span>
                </div>
                <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", columnGap: 16, minWidth: 224 }}>
                  {/* connector rail behind the nodes (submit → approve → pay) */}
                  <div style={{
                    position: "absolute", top: 4, left: "16.66%", right: "16.66%", height: 2,
                    background: "linear-gradient(90deg, #CBD5E1 0%, #93C5FD 100%)", borderRadius: 2,
                  }} />
                  {[
                    { label: "Submit", value: cycleInfo.submitBy },
                    { label: "Approve", value: cycleInfo.approveBy },
                    { label: "Pay", value: cycleInfo.pay, strong: true },
                  ].map((s) => (
                    <div key={s.label} style={{ position: "relative", textAlign: "center" }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: "50%", margin: "0 auto",
                        background: s.strong ? BLUE : "#fff",
                        border: `2px solid ${s.strong ? BLUE : "#CBD5E1"}`,
                        boxShadow: s.strong ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                      }} />
                      <div style={{ fontSize: "0.53rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.4px", marginTop: 7 }}>
                        {s.label}
                      </div>
                      <div style={{
                        fontSize: "0.76rem", fontWeight: s.strong ? 800 : 700,
                        color: s.strong ? BLUE_DARK : "#334155", marginTop: 1, whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "20px 26px", background: "#fff" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Section 1: Invoice Details ───────────────────────────── */}
          <div style={CARD_STYLE}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <SectionBadge n={1} />
              <span style={SECTION_TITLE}>Invoice Details</span>
            </div>
            <div className="row g-3">
            <div className="col-md-5">
              <Label style={LABEL_STYLE}>Invoice Title <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => set("title")(e.target.value)}
                placeholder="e.g. February cleaning service"
                style={INPUT_STYLE}
              />
              {errors.title && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div className="col-md-4">
              <Label style={LABEL_STYLE}>Vendor <span style={{ color: "#DC2626" }}>*</span></Label>
              {vendorLocked ? (
                <div style={{
                  ...INPUT_STYLE, display: "flex", alignItems: "center", gap: 8,
                  padding: "0 12px", background: "#F8FAFC", color: "#0F172A", fontWeight: 600,
                  border: "1px solid #E2E8F0",
                }}>
                  <FeatherIcon icon="user" size={14} color={BLUE} />
                  {vendorName || "Your vendor account"}
                </div>
              ) : (
                <WithIcon icon="user">
                  <Select
                    options={vendorOpts}
                    value={selectedVendor}
                    onChange={(opt) => set("vendorId")(opt?.value || "")}
                    styles={selectStyles}
                    placeholder={vendorOpts.length ? "Choose vendor" : "No vendors yet — add one first"}
                    isDisabled={vendorOpts.length === 0}
                    menuPortalTarget={document.body} menuPosition="fixed"
                  />
                </WithIcon>
              )}
              {errors.vendorId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.vendorId}</div>}
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Category <span style={{ color: "#DC2626" }}>*</span></Label>
              <WithIcon icon="tag">
                <Select
                  options={categoryChoices}
                  value={categoryChoices.find((o) => o.value === form.categoryId) || null}
                  onChange={(opt) => set("categoryId")(opt?.value || "")}
                  styles={selectStyles}
                  placeholder={
                    categoryChoices.length
                      ? "Pick a category"
                      : (form.vendorId || vendorLocked ? "Vendor has no categories" : "Pick a vendor first")
                  }
                  isDisabled={categoryChoices.length === 0}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </WithIcon>
              {errors.categoryId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.categoryId}</div>}
            </div>
            </div>{/* end row */}
          </div>{/* end Invoice Details card */}

          {/* ── Section 2: Properties & Costing ─────────────────────── */}
          <div style={CARD_STYLE}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <SectionBadge n={2} />
              <span style={SECTION_TITLE}>Properties &amp; Costing</span>
            </div>

            {isNew && totalRowCount > 1 && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10,
                padding: "8px 12px", fontSize: "0.76rem", color: BLUE_DARK,
              }}>
                <FeatherIcon icon="info" size={14} style={{ flexShrink: 0 }} />
                <span>Each unit row becomes its own invoice with its section's Invoice No. — each is approved independently.</span>
              </div>
            )}

            <input
              ref={rowFileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleRowFiles(e.target.files)}
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(form.groups || []).map((group, gi) => {
                const buildingOpts = buildingOptionsFor(group.propertyId);
                const groupTotal = (group.rows || []).reduce(
                  (a, r) => a + (computeLineMoney(computeLineTotal(r.hours, r.minutes, r.amountPerHour, r.materialCost), r.gstRate).total || 0),
                  0
                );
                return (
                  <div key={group.id} style={{
                    background: "#F8FAFC", border: "1px solid #E7EAF0", borderRadius: 12,
                    padding: "16px 16px 14px",
                  }}>
                    {/* Section header: icon · title · unit count · section total · remove */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                          width: 36, height: 36, borderRadius: "50%", background: BLUE,
                          display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
                        }}>
                          <FeatherIcon icon="home" size={16} color="#fff" />
                        </span>
                        <span style={{ fontSize: "0.88rem", fontWeight: 700, color: "#0F172A" }}>
                          Property &amp; Building
                        </span>
                        <span style={{
                          background: "#F1F5F9", color: "#64748B", borderRadius: 20,
                          padding: "3px 10px", fontSize: "0.7rem", fontWeight: 700,
                        }}>
                          {(group.rows || []).length} unit{(group.rows || []).length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          background: "#DBEAFE", color: BLUE_DARK,
                          borderRadius: 8, padding: "5px 12px", fontSize: "0.78rem", fontWeight: 800,
                        }}>
                          {formatCurrency(groupTotal)}
                        </span>
                        {isNew && form.groups.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeGroup(gi)}
                            title="Remove property section"
                            style={{
                              ...ROW_BTN, width: 30, height: 30, borderRadius: 8,
                              border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626",
                            }}
                          >
                            <FeatherIcon icon="trash-2" size={13} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Property · Building · Invoice No. */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.6fr 1.2fr",
                      gap: 12, marginBottom: 12,
                    }}>
                      <div>
                        <div style={FIELD_LABEL}>Property <span style={{ color: "#DC2626" }}>*</span></div>
                        <WithIcon icon="map-pin">
                          <Select
                            options={propertyOptions}
                            value={propertyOptions.find((o) => o.value === group.propertyId) || null}
                            onChange={(opt) => onPickProperty(gi, opt?.value || "")}
                            styles={selectStyles}
                            placeholder="Select property"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </WithIcon>
                      </div>
                      <div>
                        <div style={FIELD_LABEL}>Building <span style={{ color: "#DC2626" }}>*</span></div>
                        <WithIcon icon="grid">
                          <Select
                            options={buildingOpts}
                            value={buildingOpts.find((o) => o.value === group.buildingName) || null}
                            onChange={(opt) => updateGroup(gi, { buildingName: opt?.value || "" })}
                            styles={selectStyles}
                            placeholder={group.propertyId ? "Pick building" : "Pick property first"}
                            isDisabled={!group.propertyId}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </WithIcon>
                      </div>
                      <div>
                        <div style={FIELD_LABEL}>Invoice No.</div>
                        <WithIcon icon="hash">
                          <Input
                            value={group.referenceNo}
                            onChange={(e) => updateGroup(gi, { referenceNo: e.target.value })}
                            placeholder="Vendor invoice #"
                            style={INPUT_ICON_STYLE}
                          />
                        </WithIcon>
                      </div>
                    </div>

                    {/* Unit rows for this property */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {(group.rows || []).map((row, ri) => {
                        const subtotal = computeLineTotal(row.hours, row.minutes, row.amountPerHour, row.materialCost);
                        const m = computeLineMoney(subtotal, row.gstRate);
                        return (
                          <div key={row.id} style={{
                            background: "#fff", border: "1px solid #E7EAF0", borderRadius: 10,
                            padding: "12px 14px",
                          }}>
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: isNew ? "1fr 0.7fr 0.7fr 1fr 1fr 0.7fr 1.2fr 138px" : "1fr 0.7fr 0.7fr 1fr 1fr 0.7fr 1.2fr",
                              gap: 10,
                            }}>
                              <div>
                                <div style={FIELD_LABEL}>Unit No.</div>
                                <WithIcon icon="home">
                                  <Input
                                    value={row.unitNo}
                                    onChange={(e) => updateRow(gi, ri, { unitNo: e.target.value })}
                                    placeholder="e.g. 101"
                                    style={INPUT_ICON_STYLE}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Hours</div>
                                <WithIcon icon="clock">
                                  <Input
                                    type="number" step="1" min="0"
                                    value={row.hours}
                                    onChange={(e) => updateRow(gi, ri, { hours: e.target.value.replace(/[^0-9]/g, "") })}
                                    placeholder="0"
                                    style={INPUT_ICON_STYLE}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Minutes</div>
                                <WithIcon icon="clock">
                                  <Input
                                    type="number" step="1" min="0" max="59"
                                    value={row.minutes}
                                    onChange={(e) => {
                                      const digits = e.target.value.replace(/[^0-9]/g, "");
                                      const clamped = digits === "" ? "" : String(Math.min(59, Number(digits)));
                                      updateRow(gi, ri, { minutes: clamped });
                                    }}
                                    placeholder="0"
                                    style={INPUT_ICON_STYLE}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Amount / Hr ($)</div>
                                <WithIcon icon="dollar-sign">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={row.amountPerHour}
                                    onChange={(e) => updateRow(gi, ri, { amountPerHour: e.target.value })}
                                    placeholder="0.00"
                                    style={INPUT_ICON_STYLE}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Material Cost ($)</div>
                                <WithIcon icon="package">
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={row.materialCost}
                                    onChange={(e) => updateRow(gi, ri, { materialCost: e.target.value })}
                                    placeholder="0.00"
                                    style={INPUT_ICON_STYLE}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>GST %</div>
                                <WithIcon icon="percent">
                                  <Input
                                    type="number" step="0.1" min="0" max="100"
                                    value={row.gstRate}
                                    onChange={(e) => updateRow(gi, ri, { gstRate: e.target.value })}
                                    placeholder="0"
                                    style={INPUT_ICON_STYLE}
                                  />
                                </WithIcon>
                              </div>
                              <div>
                                <div style={FIELD_LABEL}>Line Total</div>
                                <div style={{
                                  height: 46, display: "flex", alignItems: "center", justifyContent: "flex-end",
                                  fontWeight: 800, color: "#0F172A", fontSize: "0.92rem",
                                  background: "#DBEAFE", borderRadius: 10, padding: "0 14px",
                                  border: "1px solid #BFDBFE",
                                }}>
                                  {formatCurrency(m.total)}
                                </div>
                              </div>
                              {isNew && (
                                <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
                                  <button
                                    type="button"
                                    onClick={() => openRowFilePicker(group.id, row.id)}
                                    title="Attach files to this row"
                                    style={{
                                      ...ROW_BTN,
                                      border: "1px solid #BFDBFE", background: "#fff", color: BLUE,
                                    }}
                                  >
                                    <FeatherIcon icon="paperclip" size={15} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => addRow(gi)}
                                    title="Add another unit row"
                                    style={{
                                      ...ROW_BTN,
                                      border: "1px solid #BFDBFE", background: "#fff", color: BLUE,
                                    }}
                                  >
                                    <FeatherIcon icon="plus" size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeRow(gi, ri)}
                                    disabled={(group.rows || []).length <= 1}
                                    title="Remove this row"
                                    style={{
                                      ...ROW_BTN,
                                      border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626",
                                      cursor: (group.rows || []).length <= 1 ? "not-allowed" : "pointer",
                                      opacity: (group.rows || []).length <= 1 ? 0.4 : 1,
                                    }}
                                  >
                                    <FeatherIcon icon="trash-2" size={15} />
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Note + row attachments */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                              <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200 }}>
                                <Input
                                  value={row.comment}
                                  onChange={(e) => updateRow(gi, ri, { comment: e.target.value })}
                                  placeholder="Note for this row (optional)"
                                  style={{ ...INPUT_ICON_STYLE, height: 40, fontSize: "0.8rem" }}
                                />
                                <FeatherIcon
                                  icon="align-left" size={14} color="#94A3B8"
                                  style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                                />
                              </div>
                              {(row.files || []).map((f) => (
                                <span key={f.id} style={{
                                  display: "inline-flex", alignItems: "center", gap: 6,
                                  background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 20,
                                  padding: "4px 10px", fontSize: "0.72rem", fontWeight: 600, color: BLUE_DARK,
                                  maxWidth: 220,
                                }}>
                                  {f.uploading
                                    ? <Spinner size="sm" style={{ width: 12, height: 12, flexShrink: 0 }} />
                                    : <FeatherIcon icon="paperclip" size={11} style={{ flexShrink: 0 }} />}
                                  <span
                                    onClick={() => f.signedUrl && setPreviewStaged({ signedUrl: f.signedUrl, name: f.name })}
                                    title={f.name}
                                    style={{
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                      cursor: f.signedUrl ? "zoom-in" : "default",
                                    }}
                                  >
                                    {f.name}
                                  </span>
                                  <FeatherIcon
                                    icon="x" size={11}
                                    style={{ cursor: "pointer", flexShrink: 0 }}
                                    onClick={() => removeRowFile(group.id, row.id, f.id)}
                                  />
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {isNew && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <button
                  type="button"
                  onClick={addGroup}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "#DBEAFE", border: "none", color: BLUE_DARK,
                    borderRadius: 10, padding: "10px 16px", fontSize: "0.8rem", fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <FeatherIcon icon="plus" size={14} /> Add Another Property
                </button>
              </div>
            )}
            {errors.lines && (
              <div style={{ color: "#DC2626", fontSize: "0.74rem", marginTop: 8 }}>{errors.lines}</div>
            )}

            {/* Totals summary */}
            <div style={{
              display: "flex", justifyContent: "flex-end", alignItems: "stretch",
              marginTop: 16, background: "#1E3A8A", borderRadius: 10, overflow: "hidden",
              borderLeft: "5px solid #F59E0B",
            }}>
              <div style={{ padding: "13px 22px", textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>Subtotal</div>
                <div style={{ fontSize: "1rem", color: "#fff", fontWeight: 800, marginTop: 2 }}>{formatCurrency(subtotalSum)}</div>
              </div>
              <div style={{ padding: "13px 22px", textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>GST</div>
                <div style={{ fontSize: "1rem", color: "#fff", fontWeight: 800, marginTop: 2 }}>{formatCurrency(gstSum)}</div>
              </div>
              <div style={{ padding: "13px 26px", background: "rgba(255,255,255,0.14)", textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem", color: "#FCD34D", fontWeight: 700 }}>Grand Total</div>
                <div style={{ fontSize: "1.2rem", color: "#FBBF24", fontWeight: 900, marginTop: 2 }}>{formatCurrency(totalSum)}</div>
              </div>
            </div>
          </div>{/* end Properties card */}

          {/* ── Section 3: Additional Notes ─────────────────────────── */}
          <div style={CARD_STYLE}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <SectionBadge n={3} />
              <span style={SECTION_TITLE}>Additional Notes</span>
            </div>
            <div style={{ display: "flex", border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
              <div style={{
                width: 44, background: "#FEF3C7", display: "flex",
                alignItems: "flex-start", justifyContent: "center", paddingTop: 14, flexShrink: 0,
              }}>
                <FeatherIcon icon="file-text" size={16} color="#D97706" />
              </div>
              <Input
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                placeholder="Add any extra context, scope, or instructions for this invoice…"
                style={{
                  border: "none", borderRadius: 0, boxShadow: "none",
                  fontSize: "0.85rem", minHeight: 90, padding: "12px 14px", resize: "vertical",
                }}
              />
            </div>
          </div>

          {/* ── Section 4: Invoice File ─────────────────────────────── */}
          <div style={CARD_STYLE}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <SectionBadge n={4} />
                  <div>
                    <div style={SECTION_TITLE}>Invoice File</div>
                    <div style={{ fontSize: "0.74rem", color: "#64748B", marginTop: 2, fontWeight: 400 }}>
                      Attach the single invoice file — upload first and AI can prefill the form
                    </div>
                  </div>
                </div>
                {isNew && aiScanning && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#EEF2FF", color: BLUE_DARK, border: "1px solid #C7D2FE",
                    borderRadius: 20, padding: "6px 14px", fontSize: "0.76rem", fontWeight: 700,
                  }}>
                    <Spinner size="sm" style={{ width: 14, height: 14 }} /> Reading invoice…
                  </span>
                )}
                {isNew && !aiScanning && aiScanReady && (
                  <button
                    type="button"
                    onClick={triggerManualScan}
                    title="Read this invoice with AI and prefill the form"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "linear-gradient(135deg,#6366F1,#2563EB)", border: "none", color: "#fff",
                      borderRadius: 20, padding: "7px 14px", fontSize: "0.76rem", fontWeight: 700,
                      cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.28)",
                    }}
                  >
                    <FeatherIcon icon="zap" size={13} /> Autofill with AI
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {(pendingFiles.length === 0 && attachments.length === 0) ? (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                  style={{
                    border: `2px dashed ${dragOver ? BLUE : "#CBD5E1"}`, borderRadius: 12,
                    background: dragOver ? "#EFF6FF" : "transparent",
                    padding: "26px 20px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 12, flexWrap: "wrap", transition: "all 0.12s",
                  }}
                >
                  <span style={{
                    width: 44, height: 44, borderRadius: 12, background: "#EFF6FF",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <FeatherIcon icon="paperclip" size={18} color={BLUE} />
                  </span>
                  <span style={{ color: "#334155", fontSize: "0.86rem", fontWeight: 600 }}>
                    Drag &amp; drop a file here or
                  </span>
                  <button
                    type="button"
                    disabled={uploading || saving}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      background: "#DBEAFE", border: "none", color: BLUE_DARK,
                      borderRadius: 8, padding: "8px 16px", fontSize: "0.8rem", fontWeight: 700,
                      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {uploading ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : null}
                    Choose File
                  </button>
                  <span style={{ color: "#94A3B8", fontSize: "0.78rem" }}>PDF, image, Excel, Word or CSV</span>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                  {/* Pending (uploaded to staging, not yet on the invoice) */}
                  {pendingFiles.map((pf) => {
                    const filename = pf.name;
                    const t = getFileTypeFromKey(filename);
                    const canPreview = !!pf.signedUrl && !pf.uploading;
                    return (
                      <div key={pf.id} className="iv-attach-card">
                        <div
                          className="iv-attach-card__preview"
                          onClick={() => canPreview && openPendingPreview(pf)}
                          style={{ cursor: canPreview ? "zoom-in" : "default" }}
                        >
                          {pf.uploading
                            ? <Spinner size="sm" style={{ color: BLUE }} />
                            : t === "image" && pf.signedUrl
                              ? <img src={pf.signedUrl} alt={filename} />
                              : t === "pdf"
                                ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                : t === "office"
                                  ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                  : <FeatherIcon icon="file" size={32} color="#64748B" />}
                          {pf.uploading && <span className="iv-attach-card__badge">UPLOADING</span>}
                        </div>
                        <div className="iv-attach-card__name" title={filename}>{filename}</div>
                        <div className="iv-attach-card__actions">
                          {canPreview && (
                            <button
                              type="button"
                              onClick={() => openPendingPreview(pf)}
                              title="Preview"
                            >
                              <FeatherIcon icon="eye" size={12} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemovePending(pf.id)}
                            title="Remove"
                            style={{ color: "#DC2626" }}
                            disabled={saving || pf.uploading}
                          >
                            <FeatherIcon icon="trash-2" size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Server-confirmed attachments */}
                  {attachments.map((key) => {
                    const filename = key.split("/").pop();
                    const url = signedUrls[key];
                    const t = getFileTypeFromKey(key);
                    return (
                      <div key={key} className="iv-attach-card">
                        <div className="iv-attach-card__preview" onClick={() => url && setPreviewKey(key)}>
                          {t === "image" && url
                            ? <img src={url} alt={filename} />
                            : t === "pdf"
                              ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                              : t === "office"
                                ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                : <FeatherIcon icon="file" size={32} color="#64748B" />}
                        </div>
                        <div className="iv-attach-card__name" title={filename}>{filename}</div>
                        <div className="iv-attach-card__actions">
                          <button
                            type="button"
                            onClick={() => url && setPreviewKey(key)}
                            title="Preview"
                          >
                            <FeatherIcon icon="eye" size={12} />
                          </button>
                          {url && (
                            <a href={url} target="_blank" rel="noopener noreferrer" title="Download">
                              <FeatherIcon icon="download" size={12} />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemove(key)}
                            title="Remove"
                            style={{ color: "#DC2626" }}
                          >
                            <FeatherIcon icon="trash-2" size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>{/* end Invoice File card */}

          </div>{/* end outer flex column */}
        </ModalBody>
        <ModalFooter style={{
          borderTop: "1px solid #E9EEF5", padding: "14px 24px",
          background: "#F8FAFC", borderRadius: "0 0 14px 14px",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button
            type="button"
            onClick={() => handleClose(false)}
            disabled={saving || uploading}
            style={{
              height: 44, fontWeight: 600, fontSize: "0.85rem", borderRadius: 10,
              padding: "0 22px", background: "#fff", border: "1px solid #E2E8F0", color: "#334155",
              cursor: saving || uploading ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || uploading || pendingFiles.some((p) => p.uploading) || anyRowFileUploading}
            style={{
              height: 44, fontWeight: 700, fontSize: "0.85rem", borderRadius: 10,
              padding: "0 24px", background: BLUE, border: "none", color: "#fff",
              display: "inline-flex", alignItems: "center", gap: 8,
              boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
              cursor: "pointer",
              opacity: (saving || uploading || pendingFiles.some((p) => p.uploading) || anyRowFileUploading) ? 0.6 : 1,
            }}
          >
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon={isNew ? "send" : "save"} size={15} />}
            {isNew ? "Create Invoice" : "Save Changes"}
          </button>
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        isOpen={!!previewKey}
        toggle={() => setPreviewKey(null)}
        signedUrl={previewKey ? signedUrls[previewKey] : null}
        fileKey={previewKey}
      />

      <FilePreviewModal
        isOpen={!!previewStaged}
        toggle={() => setPreviewStaged(null)}
        signedUrl={previewStaged?.signedUrl || null}
        fileKey={previewStaged?.name || null}
      />

      <PrefillReviewModal
        isOpen={!!aiSuggestion}
        suggestion={aiSuggestion}
        onConfirm={() => applySuggestion(aiSuggestion)}
        onDismiss={() => setAiSuggestion(null)}
      />
    </>
  );
};

export { InvoiceFormModal };
